import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { StudioColorFieldConfig, StudioColorPreset } from './studio-color-input.config';

@Component({
  selector: 'app-studio-color-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './studio-color-input.component.html',
  styleUrl: './studio-color-input.component.scss'
})
export class StudioColorInputComponent implements OnChanges {
  @Input() value = '';
  @Input({ required: true }) config!: StudioColorFieldConfig;
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('rootElement') private rootElementRef?: ElementRef<HTMLElement>;
  @ViewChild('valueInput') private valueInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('presetMenu') private presetMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('pickerButton') private pickerButtonRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('pickerInput') private pickerInputRef?: ElementRef<HTMLInputElement>;

  draftValue = '';
  showPresetMenu = false;
  isEditing = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.draftValue = this.value ?? '';
    }
  }

  handleInput(value: string): void {
    this.isEditing = true;
    this.draftValue = value;
    this.showPresetMenu = true;
    this.valueChange.emit(value.trim());
  }

  handleFieldKeydown(event: KeyboardEvent): void {
    if (this.isEditing || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const input = this.valueInputRef?.nativeElement;
    const preset = this.findPreset(this.draftValue);
    if (!input || !preset) {
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      this.isEditing = true;
      this.draftValue = '';
      input.value = '';
      this.valueChange.emit('');
      return;
    }

    if (event.key.length !== 1) {
      return;
    }

    event.preventDefault();
    this.isEditing = true;
    this.draftValue = event.key;
    input.value = event.key;
    this.valueChange.emit(event.key);
    queueMicrotask(() => {
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  openPresetMenu(): void {
    this.showPresetMenu = true;
  }

  selectPreset(preset: StudioColorPreset): void {
    this.isEditing = false;
    this.draftValue = preset.value;
    this.valueChange.emit(preset.value);
    this.showPresetMenu = false;
  }

  openPicker(): void {
    this.showPresetMenu = false;
    this.pickerInputRef?.nativeElement.click();
  }

  applyPickerValue(value: string): void {
    this.isEditing = false;
    this.draftValue = value;
    this.valueChange.emit(value);
  }

  handleFocusOut(event: FocusEvent): void {
    const nextTarget = event.relatedTarget as HTMLElement | null;
    if (!nextTarget) {
      return;
    }

    if (
      this.valueInputRef?.nativeElement === nextTarget
      || this.presetMenuRef?.nativeElement.contains(nextTarget)
      || this.pickerButtonRef?.nativeElement.contains(nextTarget)
    ) {
      return;
    }

    this.commitDraftValue();
    this.isEditing = false;
    this.showPresetMenu = false;
  }

  get pickerValue(): string {
    return this.resolveColorToHex(this.draftValue) ?? '#2563eb';
  }

  get swatchBackground(): string {
    return this.draftValue.trim() || 'transparent';
  }

  get displayValue(): string {
    if (this.isEditing) {
      return this.draftValue;
    }

    const preset = this.findPreset(this.draftValue);
    return preset?.label ?? this.draftValue;
  }

  get inputPlaceholder(): string {
    return this.config.placeholder ?? '';
  }

  isPresetActive(preset: StudioColorPreset): boolean {
    return this.draftValue.trim() === preset.value;
  }

  isTransparentValue(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    return normalized === '' || normalized === 'transparent' || normalized === 'rgba(0,0,0,0)' || normalized === 'rgba(0, 0, 0, 0)';
  }

  @HostListener('document:pointerdown', ['$event'])
  handleDocumentPointerDown(event: PointerEvent): void {
    const target = event.target as Node | null;
    const rootElement = this.rootElementRef?.nativeElement;
    if (!target || !rootElement || rootElement.contains(target)) {
      return;
    }

    this.commitDraftValue();
    this.isEditing = false;
    this.showPresetMenu = false;
  }

  private commitDraftValue(): void {
    this.valueChange.emit(this.draftValue.trim());
  }

  private findPreset(rawValue: string): StudioColorPreset | null {
    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      return null;
    }

    for (const section of this.config.sections) {
      const match = section.presets.find((preset) => preset.value === trimmedValue);
      if (match) {
        return match;
      }
    }

    return null;
  }

  private resolveColorToHex(rawValue: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      return null;
    }

    const probe = document.createElement('span');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.color = '';
    probe.style.color = trimmedValue;

    if (!probe.style.color) {
      return null;
    }

    document.body.appendChild(probe);
    const computedColor = window.getComputedStyle(probe).color;
    probe.remove();

    const match = computedColor.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!match) {
      return null;
    }

    return `#${[match[1], match[2], match[3]]
      .map((channel) => Number(channel).toString(16).padStart(2, '0'))
      .join('')}`;
  }
}
